import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAudit } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Settings as Cog, Users as UsersIcon, Lock, History } from "lucide-react";

const CATEGORIES = [
  { value: "all", label: "All activity" },
  { value: "lead_time", label: "Lead-time changes" },
  { value: "settings", label: "Settings changes" },
  { value: "user", label: "User management" },
];

const META = {
  lead_time: { icon: Clock, color: "text-amber-600 bg-amber-50" },
  settings: { icon: Cog, color: "text-sky-600 bg-sky-50" },
  user: { icon: UsersIcon, color: "text-violet-600 bg-violet-50" },
};

function catOf(action) {
  if (action?.startsWith("lead_time")) return "lead_time";
  if (action?.startsWith("settings")) return "settings";
  if (action?.startsWith("user")) return "user";
  return "settings";
}

export default function Audit() {
  const { isAdmin } = useAuth();
  const [cat, setCat] = useState("all");
  const { data: rows } = useQuery({
    queryKey: ["audit", cat],
    queryFn: () => getAudit(cat === "all" ? {} : { category: cat }),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="kcard p-10 text-center border-2 border-dashed border-[var(--line)]">
          <Lock className="w-9 h-9 mx-auto text-slate-300" />
          <div className="text-sm font-medium text-slate-600 mt-3">Administrator access required</div>
          <div className="text-[12px] text-slate-400 mt-1">The change audit log is only available to admin accounts.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow">Administration</div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Change Audit Log</h1>
          <p className="text-sm text-slate-500 mt-0.5">Every lead-time, settings and user change is recorded here for review.</p>
        </div>
        <div className="w-52 shrink-0">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="h-9 text-[13px]" data-testid="audit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="kcard p-0 overflow-hidden">
        <div className="divide-y divide-[var(--line)]" data-testid="audit-list">
          {(rows || []).map((r, i) => {
            const c = catOf(r.action);
            const { icon: Icon, color } = META[c] || META.settings;
            return (
              <div key={i} className="flex items-start gap-3 px-4 py-3" data-testid="audit-row">
                <div className={`w-8 h-8 grid place-items-center rounded-md shrink-0 ${color}`}><Icon className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-slate-800">{r.summary}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    <span className="font-medium text-slate-500">{r.actor_name || r.actor}</span> · {new Date(r.ts).toLocaleString()} · <span className="num">{r.action}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {(!rows || rows.length === 0) && (
            <div className="px-4 py-10 text-center text-slate-400 text-sm">
              <History className="w-8 h-8 mx-auto text-slate-300 mb-2" /> No activity recorded yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
